import React from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
const TermsOfService: React.FC = () => {
  useDocumentTitle('Cognidaily | Terms of Service');
  const lastUpdated = 'January 2025';

  return (
    <main>
      <SEOHead
        title="Terms of Service"
        description="Read Cognidaily's Terms of Service. Understand your rights and responsibilities when using our competitive trivia platform."
        keywords="terms of service, user agreement, terms and conditions, platform rules, acceptable use"
        url="https://cognidaily.io/terms-of-service"
      />

      <section className="legal-section">
        <div className="legal-container">
          <div className="legal-card">
            <h1 className="legal-main-title">Terms of Service</h1>
            <p className="legal-last-updated">Last Updated: {lastUpdated}</p>

            <div className="legal-intro">
              <p>
                Welcome to Cognidaily. These Terms of Service ("Terms") govern your access to and use of our platform. By accessing or using Cognidaily, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our platform.
              </p>
            </div>

            <div className="legal-content">
              <section className="legal-content-section">
                <h2>1. Acceptance of Terms</h2>
                
                <p>
                  By creating an account, accessing, or using Cognidaily, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms constitute a legally binding agreement between you and Cognidaily.
                </p>

                <p>
                  If you are using Cognidaily on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>2. Account Registration and Responsibilities</h2>
                
                <h3>2.1 Account Creation</h3>
                <p>
                  To use certain features of our platform, you must create an account. When creating an account, you agree to:
                </p>
                <ul>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your information to keep it accurate</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized access</li>
                </ul>

                <h3>2.2 Account Eligibility</h3>
                <p>
                  You must be at least 13 years old to create an account. By creating an account, you represent that you meet this age requirement and have the legal capacity to enter into these Terms.
                </p>

                <h3>2.3 Account Termination</h3>
                <p>
                  We reserve the right to suspend or terminate your account at any time, with or without notice, for violation of these Terms or for any other reason we deem necessary to protect the integrity of our platform.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>3. Acceptable Use Policy</h2>
                
                <p>You agree to use Cognidaily only for lawful purposes and in accordance with these Terms. You agree not to:</p>

                <h3>3.1 Prohibited Activities</h3>
                <ul>
                  <li>Attempt to cheat, manipulate, or exploit the quiz system</li>
                  <li>Use automated tools, bots, scripts, or other means to gain unfair advantages</li>
                  <li>Share answers or collaborate with others during competitive quizzes</li>
                  <li>Impersonate any person or entity or misrepresent your identity</li>
                  <li>Harass, threaten, or harm other users</li>
                  <li>Post or transmit any content that is illegal, harmful, or offensive</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Interfere with or disrupt the platform's operation</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the platform</li>
                </ul>

                <h3>3.2 Fair Competition</h3>
                <p>
                  Cognidaily is designed to provide fair and competitive quiz experiences. Any attempt to gain an unfair advantage, including but not limited to cheating, using external resources during timed quizzes, or manipulating scores, is strictly prohibited and may result in immediate account termination.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>4. Intellectual Property Rights</h2>
                
                <h3>4.1 Our Content</h3>
                <p>
                  All content on Cognidaily, including but not limited to text, graphics, logos, questions, answers, software, and code, is the property of Cognidaily or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                </p>

                <h3>4.2 Limited License</h3>
                <p>
                  We grant you a limited, non-exclusive, non-transferable, revocable license to access and use Cognidaily for personal, non-commercial purposes in accordance with these Terms. This license does not include the right to:
                </p>
                <ul>
                  <li>Reproduce, distribute, or create derivative works</li>
                  <li>Use our content for commercial purposes</li>
                  <li>Remove any copyright or proprietary notices</li>
                  <li>Scrape, crawl, or extract data from the platform</li>
                </ul>

                <h3>4.3 Your Content</h3>
                <p>
                  You retain ownership of any content you submit to Cognidaily. By submitting content, you grant us a worldwide, royalty-free, perpetual license to use, modify, and display your content in connection with operating and promoting Cognidaily.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>5. Quiz Rules and Scoring</h2>
                
                <h3>5.1 Quiz Participation</h3>
                <p>
                  When participating in quizzes, you agree to:
                </p>
                <ul>
                  <li>Answer questions independently without external assistance</li>
                  <li>Complete quizzes within the designated time limits</li>
                  <li>Accept the scoring system and results</li>
                  <li>Respect the competitive nature of the platform</li>
                </ul>

                <h3>5.2 Score Calculation</h3>
                <p>
                  Scores are calculated based on accuracy and speed. We reserve the right to adjust scores, remove invalid attempts, or recalculate rankings if we detect any irregularities or violations of these Terms.
                </p>

                <h3>5.3 Leaderboards</h3>
                <p>
                  Leaderboard rankings are determined by quiz scores and are updated in real-time. We reserve the right to remove users from leaderboards or adjust rankings for violations of these Terms or technical issues.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>6. Service Modifications and Availability</h2>
                
                <h3>6.1 Service Changes</h3>
                <p>
                  We reserve the right to modify, suspend, or discontinue any aspect of Cognidaily at any time, with or without notice. We may also impose limits on certain features or restrict access to parts of the platform.
                </p>

                <h3>6.2 Service Availability</h3>
                <p>
                  While we strive to maintain continuous availability, we do not guarantee that Cognidaily will be available at all times. The platform may be unavailable due to maintenance, technical issues, or other reasons beyond our control.
                </p>

                <h3>6.3 Updates</h3>
                <p>
                  We may release updates, new features, or changes to existing features. These updates may require you to update your browser or software to continue using the platform.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>7. Disclaimers</h2>
                
                <h3>7.1 "As Is" Service</h3>
                <p>
                  Cognidaily is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the platform will be uninterrupted, error-free, or free from viruses or other harmful components.
                </p>

                <h3>7.2 Content Accuracy</h3>
                <p>
                  While we strive to provide accurate quiz questions and content, we do not guarantee the accuracy, completeness, or reliability of any content on the platform. Quiz questions are for educational and entertainment purposes.
                </p>

                <h3>7.3 Third-Party Links</h3>
                <p>
                  Cognidaily may contain links to third-party websites or services. We are not responsible for the content, privacy practices, or terms of service of these third-party sites.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>8. Limitation of Liability</h2>
                
                <p>
                  To the maximum extent permitted by law, Cognidaily and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to your use of the platform.
                </p>

                <p>
                  Our total liability for any claims arising from your use of Cognidaily shall not exceed the amount you paid to us (if any) in the twelve months preceding the claim, or $100, whichever is greater.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>9. Indemnification</h2>
                
                <p>
                  You agree to indemnify, defend, and hold harmless Cognidaily and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to:
                </p>
                <ul>
                  <li>Your use of the platform</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                  <li>Any content you submit to the platform</li>
                </ul>
              </section>

              <section className="legal-content-section">
                <h2>10. Dispute Resolution</h2>
                
                <h3>10.1 Governing Law</h3>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of Australia, without regard to its conflict of law provisions.
                </p>

                <h3>10.2 Dispute Resolution Process</h3>
                <p>
                  If you have any dispute with Cognidaily, you agree to first contact us at <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a> to attempt to resolve the dispute informally. If we cannot resolve the dispute within 60 days, you agree to submit to the exclusive jurisdiction of the courts located in Australia.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>11. Changes to Terms</h2>
                
                <p>
                  We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of Cognidaily after such changes constitutes your acceptance of the updated Terms.
                </p>

                <p>
                  If you do not agree to the modified Terms, you must stop using the platform and may delete your account.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>12. Severability</h2>
                
                <p>
                  If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>13. Entire Agreement</h2>
                
                <p>
                  These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement between you and Cognidaily regarding your use of the platform and supersede all prior agreements and understandings.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>14. Contact Information</h2>
                
                <p>
                  If you have any questions about these Terms, please contact us at:
                </p>
                <p>
                  <strong>Email:</strong> <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsOfService;

